// The org people directory, for anywhere a person has to be picked.
//
// Reads the `directory/` projection rather than `users/` — firestore.rules
// serves `users/{uid}` only to that user (and systemAdmin), while `directory/`
// is readable by any org member precisely so features like this can search it.
//
// One shared query key means every picker on a page (followers, mentions,
// finance approvers) hits TanStack Query's cache instead of re-reading the
// whole directory each time.

import { useQuery } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import type { DirectoryEntry } from '@onlogic/shared';

import { db } from './firebase';

/** Shared across every directory consumer so they share one cached read. */
export const directoryQueryKey = ['directory'] as const;

/** One person's entry, cached per uid so every component showing them shares one read. */
export const directoryEntryQueryKey = (uid: string | undefined) => ['directoryEntry', uid] as const;

/**
 * One person's directory entry (`directory/{uid}`), or null when they are not in
 * the directory — the nightly sync removes people who have left. This is how
 * the client finds a person's name: `users/{uid}` is served only to that person
 * and systemAdmins, so reading it elsewhere shows everyone else a blank (issue 271).
 */
export function useDirectoryEntry(uid: string | undefined) {
  return useQuery({
    queryKey: directoryEntryQueryKey(uid),
    queryFn: async (): Promise<DirectoryEntry | null> => {
      const snap = await getDoc(doc(db, 'directory', uid as string));
      return snap.exists() ? (snap.data() as DirectoryEntry) : null;
    },
    enabled: !!uid,
    // Names and photos change rarely; five minutes keeps a page of approval
    // steps or cards to one read per person.
    staleTime: 5 * 60_000,
  });
}

/**
 * A person's display name from the directory, for text rather than markup (a
 * sentence, a title, an aria-label). Undefined while it loads and for someone
 * who is not in the directory; `<UserName>` is the component with a fallback.
 */
export function useUserDisplayName(uid: string | undefined): string | undefined {
  return useDirectoryEntry(uid).data?.displayName;
}

/**
 * Every person in the org directory, by display name.
 *
 * Bounded so a runaway directory can't pull an unbounded read into the client —
 * the same safety limit the Directory page uses.
 */
export async function fetchDirectory(): Promise<DirectoryEntry[]> {
  const snap = await getDocs(
    query(collection(db, 'directory'), orderBy('displayName', 'asc'), limit(2000)),
  );
  return snap.docs.map((d) => d.data() as DirectoryEntry);
}

/** Live-ish directory for pickers. Five minutes is plenty — people move rarely. */
export function useDirectory() {
  return useQuery({
    queryKey: directoryQueryKey,
    queryFn: fetchDirectory,
    staleTime: 5 * 60_000,
  });
}

/**
 * Rank directory entries against a search term, best first.
 *
 * A name-prefix match beats a word-start match, which beats a substring match
 * anywhere in the name, which beats an email match — so typing "an" surfaces
 * "Ann Lee" above "Joanna Diaz" above someone whose address merely contains it.
 * Returns [] for an empty term so a picker shows suggestions only once the user
 * starts typing.
 */
export function searchDirectory(
  entries: readonly DirectoryEntry[] | undefined,
  term: string,
  options?: { exclude?: ReadonlySet<string>; limit?: number },
): DirectoryEntry[] {
  const needle = term.trim().toLowerCase();
  if (!needle || !entries) return [];
  const exclude = options?.exclude;

  const scored: { entry: DirectoryEntry; score: number }[] = [];
  for (const entry of entries) {
    if (exclude?.has(entry.uid)) continue;
    const name = (entry.displayName ?? '').toLowerCase();
    const email = (entry.email ?? '').toLowerCase();

    let score = -1;
    if (name.startsWith(needle)) score = 0;
    else if (name.split(/\s+/).some((word) => word.startsWith(needle))) score = 1;
    else if (name.includes(needle)) score = 2;
    else if (email.includes(needle)) score = 3;
    if (score >= 0) scored.push({ entry, score });
  }

  return scored
    .sort(
      (a, b) =>
        a.score - b.score ||
        (a.entry.displayName ?? '').localeCompare(b.entry.displayName ?? ''),
    )
    .slice(0, options?.limit ?? 6)
    .map((s) => s.entry);
}
